import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { GitCompare, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 74.1 — each indicator now ships with a transparent description so
// staff/players understand what's being measured. Empty-state copy is used
// when the athlete has no underlying data (no match ratings / no PDP / no
// attendance window).
const INDICATOR_ORDER = [
  {
    key: 'form_index',
    label: 'Form',
    tooltip: 'Rolling average of the athlete\'s last 5 match evaluation ratings, scaled to 0–100.',
    emptyText: 'No match rating data yet.',
  },
  {
    key: 'availability_score',
    label: 'Availability',
    tooltip:
      'Attendance rate over the last 90 days, reduced by any currently active injury. 100 = always available.',
    emptyText: 'No attendance data yet.',
  },
  {
    key: 'development_score',
    label: 'Development',
    tooltip:
      'Average completion across all PDP goals in the current active cycle. 100 = every goal complete.',
    emptyText: 'No PDP data yet.',
  },
  {
    key: 'consistency_score',
    label: 'Consistency',
    tooltip:
      'Stability of recent match performance — derived from how tightly the last 5 ratings cluster. Higher = more consistent.',
    emptyText: 'Not enough match data to score consistency.',
  },
];

const fmt = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Math.round(Number(v));

// Phase 74.1 — only emit a percentile badge when the cohort has ≥ 2 peers
// with comparable data AND the athlete value is non-null. Otherwise hide it
// so we don't show a misleading "Top 1%" off zero peers.
const percentileBadge = (percentile, peerCount, athleteValue) => {
  if (
    percentile === null ||
    percentile === undefined ||
    athleteValue === null ||
    athleteValue === undefined ||
    !peerCount || peerCount < 2
  ) {
    return null;
  }
  if (percentile >= 80) {
    return {
      label: `Top ${Math.max(1, 100 - Math.round(percentile))}%`,
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    };
  }
  if (percentile <= 40) {
    return {
      label: `Bottom ${100 - Math.round(percentile)}%`,
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    };
  }
  return {
    label: `${Math.round(percentile)}th pct`,
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
};

const CohortCard = ({ athleteId }) => {
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [position, setPosition] = useState(null);
  const [axis, setAxis] = useState('team');

  useEffect(() => {
    let alive = true;
    if (!athleteId) return;
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/analytics/athlete/${athleteId}/cohort?by=team`, h).catch(() => null),
      axios.get(`${API}/api/analytics/athlete/${athleteId}/cohort?by=position`, h).catch(() => null),
    ]).then(([t, p]) => {
      if (!alive) return;
      setTeam(t?.data || null);
      setPosition(p?.data || null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [athleteId]);

  const current = axis === 'team' ? team : position;
  const otherLabel = axis === 'team' ? position?.cohort_label : team?.cohort_label;

  const insufficient = current && current.cohort_size < 3;

  const subtitleParts = [];
  if (team?.cohort_label) subtitleParts.push(team.cohort_label);
  if (position?.cohort_label) subtitleParts.push(position.cohort_label);
  const subtitle = subtitleParts.length ? subtitleParts.join(' · ') : '—';

  return (
    <Card className="col-span-1 md:col-span-2 lg:col-span-3" data-testid="cohort-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <GitCompare size={14} className="text-emerald-600" />
              Performance vs Similar Players
              <span
                className="inline-flex items-center"
                title="Cohort = a comparison group of similar players (same squad/team for the Team view, same position for the Position view). Scores are compared against active athletes in that group."
                data-testid="cohort-info-tooltip"
              >
                <Info size={12} className="text-gray-400 dark:text-gray-500" />
              </span>
            </CardTitle>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5" data-testid="cohort-subtitle">
              {subtitle}
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-md bg-gray-100 dark:bg-gray-800 p-0.5 text-[11px]">
            <button
              data-testid="cohort-axis-team"
              onClick={() => setAxis('team')}
              className={`px-2 py-1 rounded-sm transition ${
                axis === 'team'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Team
            </button>
            <button
              data-testid="cohort-axis-position"
              onClick={() => setAxis('position')}
              className={`px-2 py-1 rounded-sm transition ${
                axis === 'position'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Position
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="py-6 text-center text-xs text-gray-400">Loading cohort…</div>
        ) : !current ? (
          <div className="py-6 text-center text-xs text-gray-400" data-testid="cohort-error">
            Unable to load cohort
          </div>
        ) : insufficient ? (
          <div className="py-6 text-center text-xs text-gray-500 dark:text-gray-400" data-testid="cohort-insufficient">
            Not enough athletes in this cohort to compare (cohort size: {current.cohort_size}).
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {INDICATOR_ORDER.map(({ key, label, tooltip, emptyText }) => {
              const row = current.indicators?.[key] || {};
              const hasValue = row.athlete_value !== null && row.athlete_value !== undefined;
              const badge = percentileBadge(row.percentile, row.peer_count, row.athlete_value);
              return (
                <div
                  key={key}
                  className="grid grid-cols-12 gap-2 items-center py-2 text-xs"
                  data-testid={`cohort-row-${key}`}
                >
                  <span className="col-span-4 text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                    {label}
                    <span title={tooltip} className="text-gray-400" data-testid={`cohort-${key}-tooltip`}>
                      <Info size={10} />
                    </span>
                  </span>
                  {hasValue ? (
                    <>
                      <span
                        className={`col-span-2 font-semibold tabular-nums ${
                          badge ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                        }`}
                        data-testid={`cohort-${key}-value`}
                      >
                        {fmt(row.athlete_value)}
                      </span>
                      <span className="col-span-3 text-gray-400 dark:text-gray-500 tabular-nums text-[11px]" data-testid={`cohort-${key}-avg`}>
                        {row.cohort_average === null || row.cohort_average === undefined
                          ? 'no cohort data'
                          : `avg ${fmt(row.cohort_average)}`}
                      </span>
                      <span className="col-span-3 flex justify-end">
                        {badge ? (
                          <Badge className={`text-[10px] font-medium ${badge.className}`} data-testid={`cohort-${key}-percentile`}>
                            {badge.label}
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500" data-testid={`cohort-${key}-no-peers`}>
                            no peer data
                          </span>
                        )}
                      </span>
                    </>
                  ) : (
                    <span className="col-span-8 text-[11px] text-gray-400 dark:text-gray-500 italic" data-testid={`cohort-${key}-empty`}>
                      {emptyText}
                    </span>
                  )}
                </div>
              );
            })}
            <div className="pt-2 text-[10px] text-gray-400 dark:text-gray-500" data-testid="cohort-footer-note">
              Cohort: {current.cohort_size} athlete{current.cohort_size === 1 ? '' : 's'}{otherLabel ? ` · also in ${otherLabel}` : ''}.
              {' '}Comparisons require at least 2 peers with the same metric data.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CohortCard;
